import React, { Component } from "react";
import { Row, Col, Container, Modal } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import { withRouter } from "react-router-dom";
import ReactApexChart from 'react-apexcharts';
import SelectDropDown from "../selectdropdown/SelectDropDown";
import { PageCardShimmer } from '../preloader/PageCardShimmer';
import StudentPerformanceAnalysisTab from './StudentPerformanceAnalysisTab';
import AtRiskEarlyWarningTab from './AtRiskEarlyWarningTab';
import ExamReportsTab from './ExamReportsTab';
// import AttendanceReportsTab from './AttendanceReportsTab'; // hidden until backend deployed
import TestReportsTab from './TestReportsTab';
import MultiSelectDropdown from './MultiSelectDropdown';
import ExportButtons from './ExportButtons';
import { exportExcel, exportPdf } from './misExport';
import {
    defaultBranchValue,
    filterAuthorizedBranches,
    getEffectiveBranchIds,
    onBranchScopeChange,
} from "../../../utils/branchScope";
import './_managementMISReports.scss';

// Each tab is addressable by its own URL: /management-mis-reports/<slug>. The route declares
// the same slugs (App.js), and the tab bar navigates instead of only flipping state — so tabs
// are linkable, shareable, and survive back/forward + refresh.
const TABS = [
    { value: "mis", slug: "mis", label: "MIS Reports", icon: "fal fa-chart-pie" },
    { value: "exams", slug: "exams", label: "Exam Reports", icon: "fal fa-file-chart-line" },
    { value: "studentAnalysis", slug: "student-analysis", label: "Student Performance Analysis", icon: "fal fa-chart-line" },
    { value: "atRisk", slug: "at-risk", label: "At-Risk & Early Warning", icon: "fal fa-exclamation-triangle" },
    // { value: "attendance", slug: "attendance", label: "Attendance Reports", icon: "fal fa-calendar-check" }, // hidden until backend deployed
];
const MIS_BASE = "/management-mis-reports";
const DEFAULT_TAB = "mis";
// Pass mark behind the heat map's Results column. Mirrors PASS_MARK in the backend's
// getBranchScorecard — kept here only to spell the rule out in the methodology panel.
const PASS_MARK_UI = 40;
const tabFromSlug = (slug) => {
    const t = TABS.find((x) => x.slug === slug);
    return t ? t.value : DEFAULT_TAB;
};
const slugFromTab = (value) => {
    const t = TABS.find((x) => x.value === value);
    return t ? t.slug : DEFAULT_TAB;
};

// Multi-select helpers: CSV for backend params; membership test (empty = all).
const toCsv = (arr) => (arr && arr.length ? arr.join(",") : null);
const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));

const GET_BRANCHES = gql`
    query getBranches($institution_id: Int!) {
        getBranches(institution_id: $institution_id) {
            id
            branch_name
        }
    }
`;

const GET_SCOPE = gql`
    query($institution_id: Int!) {
        getInstituteBranchClasses(institute_id: $institution_id) { class_id class branch_id }
        getSectionsByBranchClasses(institute_id: $institution_id) { id section_name branch_id class_id stream_id }
        getStreamsByBranchClasses(institute_id: $institution_id) { id stream_name branch_id class_id }
        globals(institution_id: $institution_id) {
            states { state_id state_name }
            cities { id city_name }
            globalBranches { id branch_name state_id city_id }
        }
    }
`;

const GET_STUDENT_SUMMARY = gql`
    query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
        getInstituteStudentSummary(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
            students boys girls unknownGender dayScholars hostellers
        }
    }
`;

const GET_TEST_COMPLETION = gql`
    query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String) {
        getTestCompletionStatus(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id) {
            schedule_sub_type_id schedule_sub_type completedCount pendingCount totalCount percentage
        }
    }
`;

const GET_STUDENTS = gql`
    query($institution_id: Int!, $branch_id: String) {
        getNewEducationalStudents: getCombinedAcademicAndNonAcademicStudents(institution_id: $institution_id, branch_id: $branch_id) {
            student_user_id
            edu_student_id
            student_name
            student_mobile
            gender
            student_type
            class_id
            class_name
            stream_id
            stream_name
            section_id
            section_name
            branch_id
            branch_name
            roll_no
            admission_no
            father_name
            father_phone
        }
    }
`;

const GET_SUBJECT_PERF = gql`
    query($institute_id: Int!, $branch_id: String) {
        getStudentPerformanceAnalysis(institute_id: $institute_id, branch_id: $branch_id) {
            classSubjectAvg { subject values }
        }
    }
`;

const GET_MANAGEMENT_MIS_REPORTS = gql`
  query($institude_id: Int!, $branch_id: String!, $type: Int!, $class_id: String, $section_id: String, $stream_id: String) {
    getManagementMISReports(institude_id: $institude_id, branch_id: $branch_id, type: $type, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
     overallSummary {
      enrolledCount
      dropoutCount
      paidAmountPercentage
      pendingAmount
      testsDoneCount
      testsPendingCount
      avgScore
      atRiskCount
      atRiskPercentage
      attendancePercentage
    }
    feeCollectionStatus {
      totalAmount
      paidAmount
      pendingAmount
      overdueAmount
      paidCount
      partialCount
      pendingCount
      overdueCount
    }
    enrollmentTrend {
      monthKey
      enrolledCount
      monthName
      dropoutCount
    }
    testCompletionStatus {
      schedule_sub_type
      schedule_sub_type_id
      pendingCount
      totalCount
      percentage
    }
  }
  }
`;

// REQ #12 — Test-wise batch performance trend
const GET_BATCH_TREND = gql`
    query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String, $group_by: String) {
        getTestWiseBatchTrend(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id, group_by: $group_by) {
            testLabels
            batches { name data direction change }
        }
    }
`;

// REQ #13 — Dropout risk scoring per batch
const GET_DROPOUT_RISK = gql`
    query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
        getDropoutRiskByBatch(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
            batch total highRisk mediumRisk onTrack avgScore riskPct
        }
    }
`;

// Branch heat map — per-branch score across six dimensions + weighted overall
const GET_BRANCH_SCORECARD = gql`
    query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String, $include_attendance: Boolean) {
        getBranchScorecard(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id, include_attendance: $include_attendance) {
            branchId branchName revenue collections admissions academics attendance results overall
        }
    }
`;
const DATE_TYPE_OPTIONS = [
    { value: 1, label: "Today" },
    { value: 2, label: "Yesterday" },
    { value: 3, label: "Last 3 days" },
    { value: 4, label: "Last 3 months" },
    { value: 6, label: "Last year" }
];

class ManagementMISReportsSection extends Component {
    constructor(props) {
        super(props);
        const initialTab = tabFromSlug(props.match && props.match.params && props.match.params.tab);
        this.state = {
            // Driven by the URL slug (/management-mis-reports/<slug>); defaults to "mis".
            activeTab: initialTab,
            // Tabs that have been opened at least once. They stay mounted (hidden) after the
            // first visit so switching back is instant instead of a fresh network-only refetch.
            visited: { [initialTab]: true },
            scope: { states: [], cities: [], branches: [], classes: [], sections: [] },
            filters: { state: [], city: [], branch: [], class: [], stream: [], section: [] }, // cascading scope selections (multi-select arrays)
            summary: null, // { students, boys, girls, unknownGender, dayScholars, hostellers }
            testCompletion: null, // dynamic test-completion rows (online institutes)
            students: [], // student rows for the list
            expandedStudentId: null, // row expanded to show details
            studentSearch: "", // search term for the student list
            activeDetail: null, // which summary card's detail panel is open
            detailPage: 1, // pagination page within the detail popup
            subjectPerf: [], // [{ name, value }] real subject accuracy for the faculty chart
            selectedBranch: null,
            selectedType: { value: 4, label: "Last 3 months" }, // Default to Last 3 months
            branches: [],
            loading: true,
            reportsData: null,
            batchTrend: null,   // REQ #12 test-wise batch trend
            btGroupBy: "class", // batch-trend series: "class" | "exam" (NEET/JEE via exam_type)
            dropoutRisk: [],    // REQ #13 dropout risk per batch
            branchScorecard: [], // branch heat map — per-branch six-dimension scores
            loadingScorecard: false, // scorecard rides the secondary (non-blocking) wave
            loadingAttendance: false, // true while the on-demand attendance fetch is in flight
            attendanceLoaded: false,  // attendance is the slow full-scan — load it only on request
            error: null
        };
    }

    // Keep the tab in step with the URL so back/forward and pasted links work.
    componentDidUpdate(prevProps) {
        const prev = prevProps.match && prevProps.match.params ? prevProps.match.params.tab : undefined;
        const curr = this.props.match && this.props.match.params ? this.props.match.params.tab : undefined;
        if (prev !== curr) {
            const next = tabFromSlug(curr);
            if (next !== this.state.activeTab) this.setState((s) => ({ activeTab: next, visited: { ...s.visited, [next]: true } }));
        }
    }

    // Switch tab by navigating — the URL is the source of truth. Mark the tab visited so it
    // mounts (and stays mounted) instead of remounting/refetching on every switch.
    goTab = (value) => {
        this.setState((s) => ({ visited: { ...s.visited, [value]: true } }));
        const slug = slugFromTab(value);
        if (this.props.history) this.props.history.push(`${MIS_BASE}/${slug}`);
        else this.setState({ activeTab: value });
    };

    // Re-scope and reload whenever the header's branch multi-select changes. Class /
    // Stream / Section cascade off the branch set, so they are re-filled for the new one.
    componentWillUnmount() {
        if (this.offBranchScope) this.offBranchScope();
    }

    applyHeaderBranchScope = () => {
        // State/City are derived from the header's branches, so re-seed them (all selected)
        // for the new branch set, then re-fill Class / Stream / Section beneath.
        const { state, city } = this.stateCityForBranches();
        const next = { ...this.state.filters, state, city };
        next.branch = this.scopedBranchIds(next);
        if (!next.branch.length) return;
        this.autoSelectDownstream(next, 2); // 2 = "branch" in the cascade order
        // Hand the child tabs a NEW scope identity too: each already re-defaults its own
        // selection on `prevProps.scope !== this.props.scope`, so this makes every tab
        // (Exam Reports, Student Performance, At-Risk, Comparisons) pick up the new
        // header branches and refetch — without needing a change in each one.
        this.setState({ filters: next, scope: { ...this.state.scope }, expandedStudentId: null }, () => {
            this.loadScopeSummary();
            this.loadStudents();
            this.loadTestCompletion();
            this.fetchReports();
        });
    };

    async componentDidMount() {
        this.offBranchScope = onBranchScopeChange(() => this.applyHeaderBranchScope());
        try {
            const instId = parseInt(Cookies.get("institutionid")) || 118;
            const result = await this.props.client.query({
                query: GET_BRANCHES,
                variables: { institution_id: instId }
            });
            // Restrict to the branches the logged-in user is authorized for
            // (branch_id cookie). Unrestricted users keep the full list.
            const branches = filterAuthorizedBranches(result.data?.getBranches || []);

            // Set default selected branch
            let defaultBranch = { value: "all", label: "All Branches" };
            // When the user is scoped to a single authorized branch, pre-select it;
            // otherwise fall back to the "All Branches" sentinel.
            const cookieBranchId = defaultBranchValue("");
            if (cookieBranchId) {
                const found = branches.find(b => String(b.id) === String(cookieBranchId));
                if (found) {
                    defaultBranch = { value: String(found.id), label: found.branch_name };
                }
            }

            this.setState({
                branches,
                selectedBranch: defaultBranch
            }, () => {
                this.fetchReports();
            });

            // Scope filter options (states / cities / branches / classes / sections)
            try {
                const scopeRes = await this.props.client.query({
                    query: GET_SCOPE,
                    variables: { institution_id: instId },
                });
                const d = scopeRes.data || {};
                // Branches carry state_id + city_id (globalBranches) — drives the cascade.
                // Restrict to the user's authorized branches (branch_id cookie) so the
                // Branch filter — and the State/City lists derived from it below, and every
                // sub-tab that reads this scope — only ever shows branches the user can access.
                const branches = filterAuthorizedBranches(
                    (d.globals?.globalBranches || []).map((b) => ({
                        id: b.id, branch_name: b.branch_name, state_id: b.state_id, city_id: b.city_id,
                    }))
                );
                const stateIds = new Set(branches.map((b) => String(b.state_id)).filter((x) => x && x !== "0"));
                const cityIds = new Set(branches.map((b) => String(b.city_id)).filter((x) => x && x !== "0"));
                const states = (d.globals?.states || []).filter((s) => stateIds.has(String(s.state_id)));
                const cities = (d.globals?.cities || []).filter((c) => cityIds.has(String(c.id)));
                // Classes from branch-classes config — keep branch_id so Class can cascade off Branch.
                const classes = (d.getInstituteBranchClasses || [])
                    .filter((c) => c.class_id)
                    .map((c) => ({ id: c.class_id, class: c.class, branch_id: c.branch_id }));
                const sections = (d.getSectionsByBranchClasses || []);
                // Streams carry branch_id + class_id so Stream can cascade off Class.
                const streams = (d.getStreamsByBranchClasses || []).filter((s) => s.id);
                // Default: everything selected so the full institute data shows on load;
                // the user narrows by unchecking. Each level still cascades (clearing a
                // parent disables/clears its descendants → 0).
                // Branch comes from the header multi-select; State/City are DERIVED from those
                // branches (all selected by default) rather than being the path down to Branch.
                const seed = this.stateCityForBranches(branches);
                const defFilters = {
                    state: seed.state,
                    city: seed.city,
                    branch: this.scopedBranchIds(seed, branches),
                    class: [...new Set(classes.map((c) => c.id))],
                    stream: [...new Set(streams.map((s) => s.id))],
                    section: sections.map((s) => s.id),
                };
                // Set scope first, then load everything for the default (all-selected) scope.
                this.setState({ scope: { states, cities, branches, classes, streams, sections }, filters: defFilters }, () => {
                    this.loadScopeSummary();
                    this.loadStudents();
                    this.loadTestCompletion();
                    this.fetchReports(); // KPI cards + charts must load for the default scope too
                });
            } catch (e) { console.error("scope load", e); }

            // Subject-wise performance (real subject accuracy) for the faculty chart
            try {
                const spRes = await this.props.client.query({
                    query: GET_SUBJECT_PERF,
                    variables: { institute_id: instId, branch_id: Cookies.get("branch_id") ? String(Cookies.get("branch_id")) : null },
                    fetchPolicy: "network-only",
                });
                const csa = (spRes.data && spRes.data.getStudentPerformanceAnalysis && spRes.data.getStudentPerformanceAnalysis.classSubjectAvg) || [];
                const subjectPerf = csa.map((x) => {
                    const vals = (x.values || []).filter((v) => v != null);
                    return { name: x.subject, value: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0 };
                });
                this.setState({ subjectPerf });
            } catch (e) { console.error("subjectPerf load", e); }
        } catch (err) {
            console.error("Error loading branches:", err);
            this.setState({ error: "Failed to load branches", loading: false });
        }
    }

    // Cascading scope change: setting a level clears everything downstream of it.
    handleScopeChange = (level, values) => {
        const order = ["state", "city", "branch", "class", "stream", "section"];
        const idx = order.indexOf(level);
        const next = { ...this.state.filters, [level]: values || [] };
        // clear everything downstream, then — if this level now has a selection — auto-select
        // ALL valid options for every deeper level so drilling in fills the rest (you narrow by
        // unchecking). Clearing a level (empty) leaves its descendants empty → 0.
        for (let i = idx + 1; i < order.length; i++) next[order[i]] = [];
        if ((values || []).length) this.autoSelectDownstream(next, idx);
        this.setState({ filters: next, expandedStudentId: null }, () => {
            this.loadScopeSummary();
            this.loadStudents();
            this.loadTestCompletion();
            this.fetchReports(); // KPI cards + batch trend + dropout risk must drill down too
        });
    };

    // Fill every level below `fromIdx` with all its options, scoped to the choices above it.
    // Mutates `f` in place (city→branch→class→stream→section, each using the level just filled).
    autoSelectDownstream = (f, fromIdx) => {
        const sc = this.state.scope || {};
        const uniq = (arr) => { const seen = new Set(); return arr.filter((x) => { const k = String(x); if (seen.has(k)) return false; seen.add(k); return true; }); };
        if (fromIdx < 1) {
            const cityIds = new Set((sc.branches || []).filter((b) => inSel(f.state, b.state_id)).map((b) => String(b.city_id)));
            f.city = (sc.cities || []).filter((c) => cityIds.has(String(c.id))).map((c) => c.id);
        }
        if (fromIdx < 2) {
            // Branch comes from the header multi-select, narrowed by State/City above.
            f.branch = this.scopedBranchIds(f);
        }
        if (fromIdx < 3) {
            f.class = uniq((sc.classes || []).filter((c) => inSel(f.branch, c.branch_id)).map((c) => c.id));
        }
        if (fromIdx < 4) {
            f.stream = uniq((sc.streams || []).filter((s) => inSel(f.branch, s.branch_id) && inSel(f.class, s.class_id)).map((s) => s.id));
        }
        if (fromIdx < 5) {
            // Match sectionOpts exactly: sections cascade off Branch + Class AND the selected
            // Stream (a section with no stream always passes), so the selected count lines up
            // with the options actually shown.
            const passStream = (val) => (val == null || String(val) === "" || String(val) === "0") ? true : inSel(f.stream, val);
            f.section = (sc.sections || []).filter((s) => inSel(f.branch, s.branch_id) && inSel(f.class, s.class_id) && passStream(s.stream_id)).map((s) => s.id);
        }
    };

    // Effective branch ids from the drill-down: nothing is pre-selected, so an empty
    // State means nothing is in scope yet (0 count). Once State is picked, each deeper
    // level that is still empty means "all within the scope chosen so far".
    // Branch scope comes from the HEADER multi-select (utils/branchScope), not from a
    // filter on this page — filters.branch simply mirrors that selection, so every
    // existing toCsv(f.branch) call site keeps working unchanged.
    effectiveBranchIds = () => {
        const known = new Set((this.state.scope.branches || []).map((b) => String(b.id)));
        return (this.state.filters.branch || []).filter((id) => known.has(String(id)));
    };

    // Branches in scope = the HEADER's branch multi-select, then narrowed by this page's
    // State / City drill-down. Branch itself is no longer a filter here, but State and City
    // still refine which of the header's branches apply.
    // The branches the HEADER has selected (before any State/City refinement). This is the
    // root of the page's scope: State and City options are derived FROM these branches.
    headerBranchList = (branches) => {
        const all = branches || this.state.scope.branches || [];
        const sel = new Set(getEffectiveBranchIds().map(String));
        const scoped = sel.size ? all.filter((b) => sel.has(String(b.id))) : all;
        // A header selection with no overlap here (e.g. another institute) shouldn't blank
        // the page — fall back to everything this user can see.
        return scoped.length ? scoped : all;
    };

    // The header's branches, then narrowed by this page's State / City refinement.
    scopedBranchIds = (f, branches) => {
        let scoped = this.headerBranchList(branches);
        const ff = f || this.state.filters || {};
        if ((ff.state || []).length) scoped = scoped.filter((b) => inSel(ff.state, b.state_id));
        if ((ff.city || []).length) scoped = scoped.filter((b) => inSel(ff.city, b.city_id));
        return scoped.map((b) => b.id);
    };

    // State / City that exist WITHIN the header's branch selection (all selected by default).
    stateCityForBranches = (branches) => {
        const hb = this.headerBranchList(branches);
        const uniq = (arr) => [...new Set(arr.filter((x) => x && String(x) !== "0"))];
        return { state: uniq(hb.map((b) => b.state_id)), city: uniq(hb.map((b) => b.city_id)) };
    };
    effectiveBranchCsv = () => this.effectiveBranchIds().join(",");

    loadScopeSummary = async () => {
        const instId = parseInt(Cookies.get("institutionid")) || 118;
        const f = this.state.filters;
        try {
            const res = await this.props.client.query({
                query: GET_STUDENT_SUMMARY,
                variables: {
                    institute_id: instId,
                    branch_id: toCsv(f.branch),
                    class_id: toCsv(f.class),
                    section_id: toCsv(f.section),
                    stream_id: toCsv(f.stream),
                },
                fetchPolicy: "network-only",
            });
            this.setState({ summary: (res.data && res.data.getInstituteStudentSummary) || null });
        } catch (e) { console.error("summary load", e); }
    };

    loadStudents = async () => {
        const instId = parseInt(Cookies.get("institutionid")) || 118;
        // getCombinedAcademicAndNonAcademicStudents needs a branch CSV; use the effective
        // branch set (state/city/branch cascade). Nothing in scope yet → empty roster (0 count).
        const branchId = this.effectiveBranchCsv();
        if (!branchId) { this.setState({ students: [] }); return; }
        try {
            const res = await this.props.client.query({
                query: GET_STUDENTS,
                variables: { institution_id: instId, branch_id: branchId },
                fetchPolicy: "network-only",
            });
            const raw = (res.data && res.data.getNewEducationalStudents) || [];
            const students = raw.map((s, i) => ({
                id: String(s.student_user_id || s.edu_student_id || `${s.student_mobile}-${i}`),
                name: s.student_name || "", mobile: s.student_mobile || "", gender: s.gender || "",
                studentType: s.student_type, // 1 = day scholar, 2 = residential/hosteller
                className: s.class_name || "", sectionName: s.section_name || "", branchName: s.branch_name || "",
                streamName: s.stream_name || "",
                rollNo: s.roll_no || "", admissionNo: s.admission_no || "", fatherName: s.father_name || "", fatherPhone: s.father_phone || "",
                class_id: s.class_id, section_id: s.section_id, stream_id: s.stream_id, branch_id: s.branch_id,
            }));
            this.setState({ students });
        } catch (e) { console.error("students load", e); }
    };

    // Client-side class/stream/section filtering (branch is applied in the query).
    // A null/empty attribute (e.g. online students have no section/stream) always
    // passes — you can't filter a student by an attribute they don't have.
    getFilteredStudents = () => {
        const f = this.state.filters;
        // Drill-down: nothing in scope (State not yet chosen) → 0 students.
        const branchSet = new Set(this.effectiveBranchIds().map((id) => String(id)));
        if (!branchSet.size) return [];
        // A deeper level with no selection means "all within scope"; a null attribute
        // on the student (e.g. no section/stream) always passes.
        const pass = (arr, val) =>
            (val === null || val === undefined || String(val) === "" || String(val) === "0")
                ? true
                : inSel(arr, val);
        return (this.state.students || []).filter((s) =>
            branchSet.has(String(s.branch_id)) &&
            pass(f.class, s.class_id) &&
            pass(f.stream, s.stream_id) &&
            pass(f.section, s.section_id));
    };

    // Export the current (filtered) student list — Excel + PDF.
    exportTables = () => {
        const list = this.getFilteredStudents();
        return [{
            heading: `Students (${list.length})`,
            columns: ["Name", "Adm No", "Roll No", "Class", "Section", "Stream", "Branch", "Gender", "Mobile", "Father", "Father Phone"],
            rows: list.map((s) => [s.name, s.admissionNo, s.rollNo, s.className, s.sectionName, s.streamName, s.branchName, s.gender, s.mobile, s.fatherName, s.fatherPhone]),
        }];
    };

    handleExcel = () => exportExcel(this.exportTables().map((x) => ({ name: "Students", aoa: [x.columns, ...x.rows] })), "mis_students.xlsx");
    handlePdf = () => exportPdf("Management MIS Reports — Students", this.exportTables(), "mis_students.pdf");

    loadTestCompletion = async () => {
        const instId = parseInt(Cookies.get("institutionid")) || 118;
        const f = this.state.filters;
        // Nothing in scope → no tests.
        if (!this.effectiveBranchCsv()) { this.setState({ testCompletion: [] }); return; }
        try {
            const res = await this.props.client.query({
                query: GET_TEST_COMPLETION,
                variables: {
                    institute_id: instId,
                    branch_id: this.effectiveBranchCsv(),
                    class_id: toCsv(f.class),
                    section_id: toCsv(f.section),
                },
                fetchPolicy: "network-only",
            });
            this.setState({ testCompletion: (res.data && res.data.getTestCompletionStatus) || null });
        } catch (e) { console.error("test completion load", e); }
    };

    // Navigate to the individual student's performance profile (/student-profile-1/overview).
    openStudentProfile = (s) => {
        const student = {
            student_user_id: s.id,
            student_name: s.name,
            class_name: s.className,
            section_name: s.sectionName,
            branch_name: s.branchName,
            stream_name: s.streamName,
            roll_no: s.rollNo,
            admission_no: s.admissionNo,
            contact_no: s.mobile,
            student_mobile: s.mobile,
            gender: s.gender,
            father_name: s.fatherName,
            father_phone: s.fatherPhone,
            is_academic_exist: !!s.className,
        };
        this.props.history.push("/student-profile-1/overview", { student });
    };

    renderStudentsTable = (list) => {
        const all = list || this.getFilteredStudents();
        // Search by name / mobile / roll no / admission no / class / section.
        const q = (this.state.studentSearch || "").trim().toLowerCase();
        const students = q
            ? all.filter((s) => [s.name, s.mobile, s.rollNo, s.admissionNo, s.className, s.sectionName]
                .some((v) => String(v || "").toLowerCase().includes(q)))
            : all;
        const size = 10;
        const page = Math.min(this.state.detailPage, Math.max(1, Math.ceil(students.length / size)));
        const pageStudents = students.slice((page - 1) * size, page * size);
        const exp = this.state.expandedStudentId;
        const gColor = (g) => (g === "Boy" ? "#2563eb" : g === "Girl" ? "#ec4899" : "#94a3b8");
        const td = { fontSize: 13, color: "#334155", padding: "10px", borderBottom: "1px solid #f1f5f9" };
        const detail = (label, val) => (
            <div>
                <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: 13, color: "#0f172a", marginTop: 2 }}>{val || "—"}</div>
            </div>
        );
        return (
            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                    <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 360 }}>
                        <i className="fal fa-search" style={{ position: "absolute", left: 12, top: 11, color: "#9ca3af", fontSize: 13 }} />
                        <input
                            type="text"
                            value={this.state.studentSearch}
                            onChange={(e) => this.setState({ studentSearch: e.target.value, detailPage: 1 })}
                            placeholder="Search name, mobile, roll no, admission no…"
                            style={{ width: "100%", borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "8px 32px 8px 32px", fontSize: 13, color: "#1f2937", background: "#f9fafb" }}
                        />
                        {this.state.studentSearch ? (
                            <i className="fal fa-times" onClick={() => this.setState({ studentSearch: "", detailPage: 1 })}
                                style={{ position: "absolute", right: 12, top: 11, color: "#9ca3af", fontSize: 13, cursor: "pointer" }} />
                        ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}><i className="fal fa-hand-pointer" /> Click a row for details</div>
                </div>
                {students.length === 0 ? (
                    <div style={{ padding: "28px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No students</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    {["Student", "Class", "Section", "Branch", "Gender"].map((h) => (
                                        <th key={h} style={{ textAlign: "left", fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #ede9fe" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageStudents.map((s) => {
                                    const c = gColor(s.gender);
                                    const open = exp === s.id;
                                    return (
                                        <React.Fragment key={s.id}>
                                            <tr onClick={() => this.setState({ expandedStudentId: open ? null : s.id })}
                                                style={{ cursor: "pointer", background: open ? "#f5f3ff" : "transparent" }}>
                                                <td style={{ ...td, fontWeight: 600, color: "#0f172a", borderLeft: `3px solid ${c}` }}>
                                                    {s.name}<div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>{s.mobile}</div>
                                                </td>
                                                <td style={td}>{s.className || "—"}</td>
                                                <td style={td}>{s.sectionName || "—"}</td>
                                                <td style={td}>{s.branchName || "—"}</td>
                                                <td style={td}><span style={{ background: c + "1a", color: c, borderRadius: 12, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{s.gender || "—"}</span></td>
                                            </tr>
                                            {open && (
                                                <tr>
                                                    <td colSpan={5} style={{ background: "#f8fafc", padding: "14px 16px", borderBottom: "1px solid #f1f5f9", borderLeft: `3px solid ${c}` }}>
                                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
                                                            {detail("Gender", s.gender)}
                                                            {detail("Type", Number(s.studentType) === 1 ? "Day Scholar" : Number(s.studentType) === 2 ? "Residential" : "")}
                                                            {detail("Stream", s.streamName)}
                                                            {detail("Roll No", s.rollNo)}
                                                            {detail("Admission No", s.admissionNo)}
                                                            {detail("Mobile", s.mobile)}
                                                            {detail("Father", s.fatherName)}
                                                            {detail("Father Phone", s.fatherPhone)}
                                                            {detail("Branch", s.branchName)}
                                                        </div>
                                                        <div style={{ marginTop: 14 }}>
                                                            <button onClick={(e) => { e.stopPropagation(); this.openStudentProfile(s); }}
                                                                style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                                                                <i className="fal fa-chart-line" /> View Performance Profile
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {students.length > 0 ? this.renderPager(students.length) : null}
            </div>
        );
    };

    renderScopeFilters = () => {
        const sc = this.state.scope || {};
        const f = this.state.filters;
        // Cascade: each level's options are scoped to the (multi) selections above it.
        // State and City are DERIVED FROM the header's branch selection — only the states and
        // cities those branches actually sit in are offered. City additionally narrows by the
        // chosen State.
        const hb = this.headerBranchList();
        const stateIdsInScope = new Set(hb.map((b) => String(b.state_id)));
        const stateOpts = (sc.states || []).filter((s) => stateIdsInScope.has(String(s.state_id)));
        const cityIdsInScope = new Set(
            (f.state.length ? hb.filter((b) => inSel(f.state, b.state_id)) : hb).map((b) => String(b.city_id)),
        );
        const cityOpts = (sc.cities || []).filter((c) => cityIdsInScope.has(String(c.id)));
        const seenC = new Set();
        const classOpts = (sc.classes || [])
            .filter((c) => inSel(f.branch, c.branch_id))
            .filter((c) => { const k = String(c.id); if (seenC.has(k)) return false; seenC.add(k); return true; });
        // Section cascades off Branch + Class AND the selected Stream (sections carry stream_id).
        // A section with no stream (non-stream classes) always passes the stream filter.
        const passStream = (val) =>
            (val == null || String(val) === "" || String(val) === "0") ? true : inSel(f.stream, val);
        const sectionOpts = (sc.sections || []).filter((s) =>
            inSel(f.branch, s.branch_id) && inSel(f.class, s.class_id) && passStream(s.stream_id));
        // Stream options from the scope's streams, cascaded off the selected Branch + Class.
        const seenS = new Set();
        const streamOpts = (sc.streams || [])
            .filter((s) => inSel(f.branch, s.branch_id) && inSel(f.class, s.class_id))
            .filter((s) => { const k = String(s.id); if (seenS.has(k)) return false; seenS.add(k); return true; })
            .map((s) => ({ id: s.id, name: s.stream_name || `Stream ${s.id}` }));

        // Branch is NOT a filter here — it comes from the header multi-select. State and City
        // still refine which of those branches apply, and Class cascades off the result.
        const en = {
            state: true,
            city: f.state.length > 0,
            class: (f.branch || []).length > 0,
            stream: f.class.length > 0,
            section: f.class.length > 0,
        };

        return (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "14px 16px", margin: "16px 0" }}>
                <MultiSelectDropdown label="State" placeholder="Select State" value={f.state} onChange={(v) => this.handleScopeChange("state", v)}
                    options={stateOpts.map((s) => ({ value: s.state_id, label: s.state_name }))} />
                <MultiSelectDropdown label="City" placeholder={en.city ? "Select City" : "Select State first"} disabled={!en.city} value={f.city} onChange={(v) => this.handleScopeChange("city", v)}
                    options={cityOpts.map((c) => ({ value: c.id, label: c.city_name }))} />
                <MultiSelectDropdown label="Class" placeholder={en.class ? "Select Class" : "No branches in scope"} disabled={!en.class} value={f.class} onChange={(v) => this.handleScopeChange("class", v)}
                    options={classOpts.map((c) => ({ value: c.id, label: c.class }))} />
                <MultiSelectDropdown label="Stream" placeholder={en.stream ? "All Streams" : "Select Class first"} disabled={!en.stream} value={f.stream} onChange={(v) => this.handleScopeChange("stream", v)}
                    options={streamOpts.map((s) => ({ value: s.id, label: s.name }))} />
                <MultiSelectDropdown label="Section" placeholder={en.section ? "All Sections" : "Select Class first"} disabled={!en.section} value={f.section} onChange={(v) => this.handleScopeChange("section", v)}
                    options={sectionOpts.map((s) => ({ value: s.id, label: s.section_name }))} />
            </div>
        );
    };

    renderScopeCards = () => {
        const sc = this.state.scope || {};
        const f = this.state.filters;
        // Drill-down scope: the effective branch set is empty until State is chosen, so every
        // card reads 0 with nothing selected and fills in as the hierarchy is narrowed. A
        // still-empty deeper level (Class/Section) means "all within the scope chosen so far".
        const effIds = this.effectiveBranchIds();
        const effSet = new Set(effIds.map((id) => String(id)));
        const inEff = (bid) => effSet.has(String(bid));
        const branchesFiltered = (sc.branches || []).filter((b) => inEff(b.id));
        const branchCount = branchesFiltered.length;
        const cityCount = new Set(branchesFiltered.map((b) => String(b.city_id)).filter((x) => x && x !== "0")).size;
        const seenC = new Set();
        const classCount = !effSet.size ? 0 : (sc.classes || [])
            .filter((c) => inEff(c.branch_id))
            .filter((c) => { const k = String(c.id); if (seenC.has(k)) return false; seenC.add(k); return true; })
            .filter((c) => inSel(f.class, c.id)).length;
        const sectionCount = !effSet.size ? 0 : (sc.sections || []).filter((s) =>
            inEff(s.branch_id) &&
            inSel(f.class, s.class_id) &&
            inSel(f.section, s.id)).length;

        const num = (n) => (Number(n) || 0).toLocaleString();
        // Student counts derived from the client-filtered student list (the combined academic +
        // non-academic roster) — the server summary undercounts (it requires is_admitted=1).
        const fs = this.getFilteredStudents();
        const isBoy = (g) => { g = (g || "").toLowerCase(); return g.startsWith("b") || g === "male" || g === "m"; };
        const isGirl = (g) => { g = (g || "").toLowerCase(); return g.startsWith("g") || g === "female" || g === "f"; };
        const students = fs.length;
        const boys = fs.filter((s) => isBoy(s.gender)).length;
        const girls = fs.filter((s) => isGirl(s.gender)).length;
        const dayScholars = fs.filter((s) => Number(s.studentType) === 1).length;
        const hostellers = fs.filter((s) => Number(s.studentType) === 2).length;
        const dayBoys = fs.filter((s) => Number(s.studentType) === 1 && isBoy(s.gender)).length;
        const dayGirls = fs.filter((s) => Number(s.studentType) === 1 && isGirl(s.gender)).length;
        const hostBoys = fs.filter((s) => Number(s.studentType) === 2 && isBoy(s.gender)).length;
        const hostGirls = fs.filter((s) => Number(s.studentType) === 2 && isGirl(s.gender)).length;
        const cells = [
            { key: "students", order: 4, label: "Total Students", value: num(students), sub:`Boys ${num(boys)} · Girls ${num(girls)}` },
            { key: "dayScholars", order: 5, label: "Day Scholars", value: num(dayScholars), sub: `Boys ${num(dayBoys)} · Girls ${num(dayGirls)}` },
            { key: "hostellers", order: 6, label: "Hostellers", value: num(hostellers), sub: `Boys ${num(hostBoys)} · Girls ${num(hostGirls)}` },
            { key: "branches", order: 1, label: "Branches", value: num(branchCount), sub: cityCount ? `across ${cityCount} ${cityCount === 1 ? "city" : "cities"}` : " " },
            { key: "classes", order: 2, label: "Classes", value: num(classCount), sub: " " },
            { key: "sections", order: 3, label: "Sections", value: num(sectionCount), sub: " " },
        ];

        cells.sort((a, b) => a.order - b.order);
        const COLORS = { branches: "#6366f1", classes: "#0ea5e9", sections: "#f59e0b", students: "#16a34a", dayScholars: "#06b6d4", hostellers: "#8b5cf6" };
        // While a filter change is refetching, shimmer the numbers instead of showing stale counts.
        const updating = !!this.state.loading;

        return (
            <div style={{ display: "flex", flexWrap: "wrap", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 16, overflow: "hidden" }}>
                {cells.map((c, i) => (
                    <div key={c.key} onClick={() => this.setState({ activeDetail: this.state.activeDetail === c.key ? null : c.key, detailPage: 1, expandedStudentId: null })} style={{ flex: "1 1 0", minWidth: 150, padding: "16px 22px", cursor: "pointer", textAlign: "center", background: this.state.activeDetail === c.key ? (COLORS[c.key] || "#7c3aed") + "12" : "transparent", borderBottom: `${this.state.activeDetail === c.key ? 4 : 3}px solid ${COLORS[c.key] || "#ddd6fe"}`, borderRight: i < cells.length - 1 ? "1px solid #eef2f7" : "none" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS[c.key] || "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>{c.label}</div>
                        {updating ? (
                            <>
                                <div style={{ marginTop: 6, display: "flex", justifyContent: "center" }}><span className="mis-shim" style={{ height: 26, width: 58 }} /></div>
                                <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}><span className="mis-shim" style={{ height: 11, width: 92 }} /></div>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.15, marginTop: 4 }}>{c.value}</div>
                                <div style={{ fontSize: 12, color: c.muted ? "#cbd5e1" : "#64748b", marginTop: 4, fontStyle: c.muted ? "italic" : "normal" }}>{c.sub}</div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    renderPager = (total) => {
        const size = 10;
        const pages = Math.ceil(total / size);
        if (pages <= 1) return null;
        const page = Math.min(this.state.detailPage, pages);
        const go = (p) => this.setState({ detailPage: Math.min(Math.max(1, p), pages), expandedStudentId: null });
        const btn = (label, p, disabled, active) => (
            <button key={label} disabled={disabled} onClick={() => go(p)}
                style={{ border: "1px solid " + (active ? "#7c3aed" : "#e5e7eb"), background: active ? "#7c3aed" : "#fff", color: active ? "#fff" : (disabled ? "#cbd5e1" : "#334155"), borderRadius: 8, padding: "5px 11px", fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", margin: "0 3px" }}>{label}</button>
        );
        const nums = [];
        const start = Math.max(1, Math.min(page - 2, pages - 4));
        for (let p = start; p <= Math.min(pages, start + 4); p++) nums.push(p);
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>Page {page} of {pages} · {total} total</span>
                <div>
                    {btn("Prev", page - 1, page <= 1)}
                    {nums.map((p) => btn(String(p), p, false, p === page))}
                    {btn("Next", page + 1, page >= pages)}
                </div>
            </div>
        );
    };

    renderScopeDetail = () => {
        const ad = this.state.activeDetail;
        const sc = this.state.scope || {};
        const f = this.state.filters;
        const close = () => this.setState({ activeDetail: null, expandedStudentId: null });
        const cityNameById = {}; (sc.cities || []).forEach((c) => { cityNameById[String(c.id)] = c.city_name; });
        const classNameById = {}; (sc.classes || []).forEach((c) => { classNameById[String(c.id)] = c.class; });
        const branchNameById = {}; (sc.branches || []).forEach((b) => { branchNameById[String(b.id)] = b.branch_name; });
        // Student counts per branch / class / section (from the loaded student list).
        const allStudents = this.state.students || [];
        const countBy = (key, val) => allStudents.filter((s) => String(s[key]) === String(val)).length;
        const tbl = (cols, rows) => {
            const size = 10;
            const page = Math.min(this.state.detailPage, Math.max(1, Math.ceil(rows.length / size)));
            const pageRows = rows.slice((page - 1) * size, page * size);
            return (
                <div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr>{cols.map((c) => <th key={c} style={{ textAlign: "left", fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #ede9fe" }}>{c}</th>)}</tr></thead>
                        <tbody>{rows.length === 0
                            ? <tr><td colSpan={cols.length} style={{ padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>None</td></tr>
                            : pageRows.map((r, i) => <tr key={i} style={{ background: i % 2 ? "#faf5ff" : "#fff" }}>{r.map((cell, j) => <td key={j} style={{ fontSize: 13, color: j === 1 ? "#0f172a" : "#334155", fontWeight: j === 1 ? 600 : 400, padding: "9px 10px", borderBottom: "1px solid #f3e8ff" }}>{cell}</td>)}</tr>)}
                        </tbody>
                    </table>
                    {this.renderPager(rows.length)}
                </div>
            );
        };

        let title = "", body = null, big = false;
        if (ad === "students") { title = `Students (${this.getFilteredStudents().length})`; body = this.renderStudentsTable(); big = true; }
        else if (ad === "dayScholars" || ad === "hostellers") {
            const type = ad === "dayScholars" ? 1 : 2;
            const list = this.getFilteredStudents().filter((s) => Number(s.studentType) === type);
            title = `${ad === "dayScholars" ? "Day Scholars" : "Hostellers"} (${list.length})`;
            body = this.renderStudentsTable(list); big = true;
        } else if (ad === "branches") {
            title = "Branches";
            const list = (sc.branches || []).filter((b) =>
                inSel(f.state, b.state_id) &&
                inSel(f.city, b.city_id) &&
                inSel(f.branch, b.id));
            body = tbl(["#", "Branch", "City", "Students"], list.map((b, i) => [i + 1, b.branch_name, cityNameById[String(b.city_id)] || "-", countBy("branch_id", b.id)]));
        } else if (ad === "classes") {
            const seen = new Set();
            title = "Classes";
            const list = (sc.classes || [])
                .filter((c) => inSel(f.branch, c.branch_id))
                .filter((c) => { const k = String(c.id); if (seen.has(k)) return false; seen.add(k); return true; })
                .filter((c) => inSel(f.class, c.id));
            body = tbl(["#", "Class", "Students"], list.map((c, i) => [i + 1, c.class, countBy("class_id", c.id)]));
        } else if (ad === "sections") {
            title = "Sections";
            const list = (sc.sections || []).filter((s) =>
                inSel(f.branch, s.branch_id) &&
                inSel(f.class, s.class_id) &&
                inSel(f.section, s.id));
            body = tbl(["#", "Section", "Class", "Branch", "Students"], list.map((s, i) => [i + 1, s.section_name, classNameById[String(s.class_id)] || "-", branchNameById[String(s.branch_id)] || "-", countBy("section_id", s.id)]));
        }

        return (
            <Modal show={!!ad} onHide={close} centered size={big ? "lg" : undefined} scrollable>
                <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)", borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ background: "#ffffff", padding: "18px 22px" }}>{body}</Modal.Body>
            </Modal>
        );
    };

    fetchReports = async () => {
        // Drill-down: nothing selected → the whole dashboard reads zero (no fallback branch).
        const branchIdStr = this.effectiveBranchCsv();
        if (!branchIdStr) {
            this.setState({ reportsData: null, batchTrend: null, dropoutRisk: [], branchScorecard: [], loadingScorecard: false, loadingAttendance: false, attendanceLoaded: false, loading: false, error: null });
            return;
        }
        this.setState({ loading: true, loadingScorecard: true, loadingAttendance: false, attendanceLoaded: false, error: null });
        try {
            const instId = parseInt(Cookies.get("institutionid")) || 118;
            const f = this.state.filters;
            const classCsv = toCsv(f.class);
            const sectionCsv = toCsv(f.section);
            const streamCsv = toCsv(f.stream);

            const typeVal = this.state.selectedType ? this.state.selectedType.value : 4;

            const result = await this.props.client.query({
                query: GET_MANAGEMENT_MIS_REPORTS,
                variables: {
                    institude_id: instId,
                    branch_id: branchIdStr,
                    type: typeVal,
                    class_id: classCsv,
                    section_id: sectionCsv,
                    stream_id: streamCsv
                },
                fetchPolicy: "network-only"
            });

            this.setState({
                reportsData: result.data?.getManagementMISReports || null,
                loading: false
            });

            // REQ #12 + #13 — fetch in parallel; non-blocking (don't fail the dashboard).
            Promise.all([
                this.props.client.query({
                    query: GET_BATCH_TREND,
                    variables: { institute_id: instId, branch_id: branchIdStr, class_id: classCsv, section_id: sectionCsv, stream_id: streamCsv, group_by: this.state.btGroupBy || "class" },
                    fetchPolicy: "network-only",
                }).then((r) => r.data?.getTestWiseBatchTrend || null).catch(() => null),
                this.props.client.query({
                    query: GET_DROPOUT_RISK,
                    variables: { institute_id: instId, branch_id: branchIdStr, class_id: classCsv, section_id: sectionCsv, stream_id: streamCsv },
                    fetchPolicy: "network-only",
                }).then((r) => r.data?.getDropoutRiskByBatch || []).catch(() => []),
                // The benchmarks query went with the Benchmarks card — it is the heaviest request
                // on this screen (per-exam state/city/branch/section breakdowns) and nothing here
                // renders it any more. Comparisons fetches it for the Section axis instead.
                // Phase 1: the five cheap dimensions (skip the slow attendance scan) so the
                // heat map renders in ~1s instead of waiting on the 800k-row attendance query.
                this.props.client.query({
                    query: GET_BRANCH_SCORECARD,
                    variables: { institute_id: instId, branch_id: branchIdStr, class_id: classCsv, section_id: sectionCsv, stream_id: streamCsv, include_attendance: false },
                    fetchPolicy: "network-only",
                }).then((r) => r.data?.getBranchScorecard || []).catch(() => []),
            ]).then(([batchTrend, dropoutRisk, branchScorecard]) => {
                this.setState(
                    { batchTrend, dropoutRisk: dropoutRisk || [], branchScorecard: branchScorecard || [], loadingScorecard: false, btClass: null },
                    // Attendance is still a SEPARATE, slower query, but it now starts on its own
                    // as soon as the cheap dimensions have rendered — the column fills in a
                    // moment later instead of waiting for someone to click "load". Kicking it off
                    // here (rather than alongside the others) keeps the first paint fast, and the
                    // manual "load" control stays as a retry if this call fails.
                    this.loadAttendanceScores,
                );
            });
        } catch (err) {
            console.error("Error fetching MIS reports:", err);
            this.setState({ error: err.message || "Failed to load reports", loading: false });
        }
    };

    handleBranchChange = (name, value) => {
        const found = this.getBranchOptions().find(opt => String(opt.value) === String(value));
        if (found) {
            this.setState({ selectedBranch: found }, () => {
                this.fetchReports();
            });
        }
    };

    handleTypeChange = (name, value) => {
        const found = DATE_TYPE_OPTIONS.find(opt => String(opt.value) === String(value));
        if (found) {
            this.setState({ selectedType: found }, () => {
                this.fetchReports();
            });
        }
    };

    getBranchOptions = () => {
        const options = [{ value: "all", label: "All Branches" }];
        this.state.branches.forEach(b => {
            options.push({ value: String(b.id), label: b.branch_name });
        });
        return options;
    };

    // --- Chart Options and Series ---

    getFacultyPerformanceChartOptions = (subjectPerf = []) => {
        return {
            chart: {
                type: 'bar',
                height: 350,
                toolbar: { show: false }
            },
            plotOptions: {
                bar: {
                    borderRadius: 6,
                    columnWidth: '45%',
                }
            },
            colors: ['#9ca3af'],
            dataLabels: { enabled: false },
            grid: {
                borderColor: '#F1F5F9',
                strokeDashArray: 3
            },
            xaxis: {
                categories: subjectPerf.map((s) => s.name),
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px',
                        fontWeight: 500
                    }
                }
            },
            yaxis: {
                max: 100,
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px'
                    }
                }
            },
            noData: { text: "No data" }
        };
    };

    getFacultyPerformanceChartSeries = (subjectPerf = []) => {
        return [{
            name: 'Performance Score',
            data: subjectPerf.map((s) => s.value)
        }];
    };

    getEnrollmentTrendChartOptions = (trendData = []) => {
        const categories = (trendData || []).map((t) => t.monthName);
        return {
            chart: {
                type: 'line',
                height: 350,
                toolbar: { show: false }
            },
            colors: ['#9ca3af', '#6b7280'],
            stroke: {
                width: 3,
                curve: 'smooth'
            },
            grid: {
                borderColor: '#F1F5F9',
                strokeDashArray: 3
            },
            markers: {
                size: 5,
                colors: ['#9ca3af', '#6b7280'],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: { size: 7 }
            },
            xaxis: {
                categories: categories.length ? categories : [],
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px',
                        fontWeight: 500
                    }
                }
            },
            yaxis: {
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px'
                    }
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'center',
                labels: { colors: '#64748B' }
            },
            noData: { text: "No data" }
        };
    };

    getEnrollmentTrendChartSeries = (trendData = []) => {
        const rows = trendData || [];
        return [
            { name: 'Enrolled', data: rows.map((t) => t.enrolledCount || 0) },
            { name: 'Dropouts', data: rows.map((t) => t.dropoutCount || 0) }
        ];
    };

    getFeeStatusChartOptions = (feeStatus) => {
        const fs = feeStatus || {};
        const totalAmount = fs.totalAmount ? fs.totalAmount / 100000 : 0;
        const paidAmount = fs.paidAmount ? fs.paidAmount / 100000 : 0;
        const pendingAmount = fs.pendingAmount ? fs.pendingAmount / 100000 : 0;
        const partialAmount = Math.max(0, totalAmount - paidAmount - pendingAmount);

        const amounts = [
            totalAmount.toFixed(1) + 'L',
            paidAmount.toFixed(1) + 'L',
            partialAmount.toFixed(1) + 'L',
            pendingAmount.toFixed(1) + 'L'
        ];

        return {
            chart: {
                type: 'bar',
                height: 350,
                toolbar: { show: false }
            },
            plotOptions: {
                bar: {
                    distributed: true,
                    borderRadius: 6,
                    columnWidth: '40%'
                }
            },
            colors: ['#7C3AED', '#10B981', '#F59E0B', '#EF4444'],
            dataLabels: {
                enabled: true,
                formatter: function (val, opts) {
                    if (opts.dataPointIndex === undefined || opts.dataPointIndex < 0 || opts.dataPointIndex >= amounts.length) {
                        return val;
                    }
                    const amountStr = amounts[opts.dataPointIndex];
                    return `${val} (₹${amountStr})`;
                },
                style: {
                    fontSize: '10px',
                    colors: ['#fff']
                }
            },
            grid: {
                borderColor: '#F1F5F9',
                strokeDashArray: 3
            },
            legend: { show: false },
            xaxis: {
                categories: ['Total', 'Paid', 'Partial', 'Pending'],
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px',
                        fontWeight: 600
                    }
                }
            },
            yaxis: {
                labels: {
                    style: {
                        colors: '#64748B',
                        fontSize: '11px'
                    }
                }
            },
            tooltip: {
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    if (dataPointIndex === undefined || dataPointIndex === null || dataPointIndex < 0 || dataPointIndex >= amounts.length) {
                        return '';
                    }
                    const category = w.globals.labels[dataPointIndex];
                    const count = series[seriesIndex][dataPointIndex];
                    const amountStr = amounts[dataPointIndex];

                    return `
                        <div class="apexcharts-tooltip-custom" style="padding: 12px; background: #1E293B; color: #fff; border-radius: 6px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); font-family: sans-serif; font-size: 12px; line-height: 1.5;">
                            <div style="font-weight: bold; margin-bottom: 6px; border-bottom: 1px solid #475569; padding-bottom: 4px; color: #F8FAFC;">
                                ${category} Status
                            </div>
                            <div style="display: flex; justify-content: space-between; gap: 16px; margin-bottom: 4px;">
                                <span style="color: #94A3B8;">Students:</span>
                                <span style="font-weight: 600; color: #38BDF8;">${count}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; gap: 16px;">
                                <span style="color: #94A3B8;">Amount:</span>
                                <span style="font-weight: 600; color: #34D399;">₹${amountStr}</span>
                            </div>
                        </div>
                    `;
                }
            }
        };
    };

    getFeeStatusChartSeries = (feeStatus) => {
        const fs = feeStatus || {};
        const paidCount = fs.paidCount || 0;
        const partialCount = fs.partialCount || 0;
        const pendingCount = fs.pendingCount || 0;
        const totalCount = paidCount + partialCount + pendingCount;

        return [
            {
                name: 'Students',
                data: [totalCount, paidCount, partialCount, pendingCount]
            }
        ];
    };

    renderTestCompletionList = (tests) => {
        if (!tests || tests.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    No test data available for this range.
                </div>
            );
        }
        return (
            <div className="test-list">
                {tests.map((test, index) => {
                    const pct = test.percentage || 0;
                    let progressColorClass = "low";
                    if (pct >= 90) progressColorClass = "high";
                    else if (pct >= 50) progressColorClass = "medium";

                    return (
                        <div className="test-list__item" key={test.schedule_sub_type_id || index}
                            style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 10, background: "#fff" }}>
                            <div className="test-list__item-header">
                                <span className="test-name">{test.schedule_sub_type || "General Test"}</span>
                                <span className="test-meta">
                                    Pending: {test.pendingCount} of {test.totalCount} · {pct}%
                                </span>
                            </div>
                            <div className="test-list__item-progress-bg">
                                <div
                                    className={`test-list__item-progress-bar ${progressColorClass}`}
                                    style={{ width: `${pct}%`, background: "#9ca3af" }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    render() {
        return (
            <React.Fragment>
                {/* Sticky control deck (mis-redesign-preview.html): the tab row pins to the
                    top so it stays reachable while a long report scrolls. Styling lives in
                    _managementMISReports.scss (.mis-deck / .mis-tabs / .mis-tab). */}
                <div className="mis-deck">
                    <div className="mis-deck__in">
                        <div className="mis-tabs" role="tablist">
                            {TABS.map((tab) => {
                                const isActive = this.state.activeTab === tab.value;
                                return (
                                    <a
                                        key={tab.value}
                                        role="tab"
                                        aria-selected={isActive}
                                        className={`mis-tab${isActive ? " on" : ""}`}
                                        // Real href so the tab is a normal, copyable/middle-clickable link.
                                        href={`${MIS_BASE}/${tab.slug}`}
                                        onClick={(e) => { e.preventDefault(); this.goTab(tab.value); }}
                                    >
                                        <i className={tab.icon} />
                                        {tab.label}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
                {/* Keep-alive tab panes: each child tab mounts on its first visit and then
                    stays mounted (hidden) so switching back is instant, not a fresh refetch.
                    The "mis" pane is the section's own inline dashboard. */}
                {(() => {
                    const active = this.state.activeTab;
                    const v = this.state.visited || {};
                    const pane = (val) => ({ display: active === val ? "block" : "none" });
                    return (
                        <React.Fragment>
                            {(v.mis || active === "mis") && <div style={pane("mis")}>{this.renderMisDashboard()}</div>}
                            {(v.exams || active === "exams") && <div style={pane("exams")}><ExamReportsTab scope={this.state.scope} /></div>}
                            {(v.studentAnalysis || active === "studentAnalysis") && <div style={pane("studentAnalysis")}><StudentPerformanceAnalysisTab scope={this.state.scope} /></div>}
                            {(v.atRisk || active === "atRisk") && <div style={pane("atRisk")}><AtRiskEarlyWarningTab scope={this.state.scope} /></div>}
                            {/* Attendance Reports hidden until backend deployed
                            {(v.attendance || active === "attendance") && <div style={pane("attendance")}><AttendanceReportsTab scope={this.state.scope} /></div>} */}
                        </React.Fragment>
                    );
                })()}
            </React.Fragment>
        );
    }

    // Attendance is the expensive full-scan dimension — fetched only when the user asks for it
    // (via the Attendance column header), so the heat map's default load stays light on the DB.
    loadAttendanceScores = () => {
        if (this.state.loadingAttendance || this.state.attendanceLoaded) return;
        const branchIdStr = this.effectiveBranchCsv();
        if (!branchIdStr) return;
        const instId = parseInt(Cookies.get("institutionid")) || 118;
        const f = this.state.filters;
        this.setState({ loadingAttendance: true });
        this.props.client.query({
            query: GET_BRANCH_SCORECARD,
            variables: { institute_id: instId, branch_id: branchIdStr, class_id: toCsv(f.class), section_id: toCsv(f.section), stream_id: toCsv(f.stream), include_attendance: true },
            fetchPolicy: "network-only",
        }).then((r) => {
            const full = r.data && r.data.getBranchScorecard ? r.data.getBranchScorecard : [];
            this.setState((prev) => ({ branchScorecard: full.length ? full : prev.branchScorecard, loadingAttendance: false, attendanceLoaded: true }));
        }).catch(() => this.setState({ loadingAttendance: false }));
    };

    // Branch heat map — per-branch score across six dimensions + weighted overall.
    // Bands: >=85 on track, 70-84 watch, 55-69 intervene, <55 critical.
    renderBranchHeatMap() {
        // Each dimension carries the formula actually used by getBranchScorecard, shown as a
        // header tooltip and spelled out in the "How these scores are calculated" panel below
        // the legend — so a number can always be traced back to how it was derived.
        // `rel` marks the two RELATIVE scores: they rank branches against the best branch in
        // the current scope rather than measuring an absolute rate, so the top branch is
        // always 100 and the value moves when the branch filter changes.
        const DIMS = [
            { key: "revenue", label: "Revenue", weight: 15, rel: true,
              formula: "How much fee money this branch collected, compared with the branch that collected the most." },
            { key: "collections", label: "Collections", weight: 20,
              formula: "Of all the fees due to this branch, how much has actually been paid." },
            { key: "admissions", label: "Admissions", weight: 15, rel: true,
              formula: "How many students have confirmed admission here, compared with the branch that has the most." },
            { key: "academics", label: "Academics", weight: 20,
              formula: "The average test score. For each test a student takes, we count the questions they got right out of the questions they answered — then average that across every test taken in the branch." },
            { key: "attendance", label: "Attendance", weight: 15,
              formula: "Taken from the biometric punch records. Out of every student-day the branch was open, how many the student was actually present for. Students who have never punched are left out rather than counted absent." },
            { key: "results", label: "Results", weight: 15,
              formula: `The pass rate. Out of every test taken in the branch, this is the percentage that scored ${PASS_MARK_UI}% or more — for example, 70 means 7 out of every 10 tests taken were a pass.` },
        ];
        const band = (v) => (v == null ? "na" : v >= 85 ? "ok" : v >= 70 ? "watch" : v >= 55 ? "int" : "crit");
        // One row per branch. The Branch / City / State grouping toggle was removed — the
        // Comparisons tab already compares by geography, and two screens offering the same
        // roll-up invited them to disagree.
        const rows = this.state.branchScorecard || [];

        return (
            <div className="mis-reports__chart-card">
                <div className="mis-reports__chart-card-header">
                    <h5 className="title"><i className="fal fa-th"></i> Performance heat map</h5>
                    <span className="mis-hm-sub">Score = weighted composite of the six dimensions.</span>
                </div>
                <div className="mis-hm-scroll">
                    <table className="mis-hm">
                        <thead>
                            <tr>
                                <th className="b">Branch</th>
                                {DIMS.map((d) => (
                                    <th key={d.key} title={`${d.formula}. Weight ${d.weight}.`}>
                                        {d.label}
                                        {d.key === "attendance" && rows.length > 0 && !this.state.attendanceLoaded && (
                                            this.state.loadingAttendance
                                                ? <i className="fal fa-spinner fa-spin mis-hm-load" />
                                                : <button type="button" className="mis-hm-load" onClick={this.loadAttendanceScores} title="Attendance is a heavy query — click to load">load</button>
                                        )}
                                    </th>
                                ))}
                                <th>Overall</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.loadingScorecard && rows.length === 0 ? (
                                [0, 1, 2, 3].map((i) => (
                                    <tr key={i}>
                                        <td className="b"><span className="mis-shim" style={{ height: 14, width: 110, display: "inline-block" }} /></td>
                                        {DIMS.map((d) => <td key={d.key}><div className="mis-hm-cell na"><span className="mis-shim" style={{ height: 14, width: 30 }} /></div></td>)}
                                        <td className="ov na"><span className="mis-shim" style={{ height: 14, width: 24, display: "inline-block" }} /></td>
                                    </tr>
                                ))
                            ) : rows.length === 0 ? (
                                <tr><td className="b" colSpan={DIMS.length + 2} style={{ textAlign: "center", color: "#94a3b8", padding: "28px 0" }}>No branch data for this selection.</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.branchId}>
                                    <td className="b">{r.branchName}</td>
                                    {DIMS.map((d) => {
                                        const v = r[d.key];
                                        // Attendance is fetched in a 2nd phase — show a shimmer there until it lands.
                                        if (d.key === "attendance" && v == null && this.state.loadingAttendance) {
                                            return <td key={d.key}><div className="mis-hm-cell na"><span className="mis-shim" style={{ height: 14, width: 30 }} /></div></td>;
                                        }
                                        return <td key={d.key}><div className={`mis-hm-cell ${band(v)}`}>{v == null ? "—" : v}</div></td>;
                                    })}
                                    <td className={`ov ${band(r.overall)}`}>{r.overall == null ? "—" : r.overall}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mis-hm-legend">
                    <span><i className="ok"></i>≥85 on track</span>
                    <span><i className="watch"></i>70–84 watch</span>
                    <span><i className="int"></i>55–69 intervene</span>
                    <span><i className="crit"></i>&lt;55 critical</span>
                </div>

                {/* Methodology — every column's formula, its weight, and how Overall combines
                    them. Collapsed by default (native <details>, no extra state) so it explains
                    on demand without taking room from the grid. */}
                <details style={{ marginTop: 10, borderTop: "1px solid #eef2f7", paddingTop: 10 }}>
                    <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#6366f1", listStyle: "revert" }}>
                        How these scores are calculated
                    </summary>
                    <div style={{ marginTop: 8 }}>
                        <table style={{ width: "100%", fontSize: 12, color: "#334155", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ color: "#6b7280", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    <th style={{ textAlign: "left", padding: "4px 8px 4px 0", width: 96 }}>Column</th>
                                    <th style={{ textAlign: "left", padding: "4px 8px 4px 0" }}>How it is calculated</th>
                                    <th style={{ textAlign: "right", padding: "4px 0", width: 56 }}>Weight</th>
                                </tr>
                            </thead>
                            <tbody>
                                {DIMS.map((d) => (
                                    <tr key={d.key} style={{ borderTop: "1px solid #f3f4f6" }}>
                                        <td style={{ padding: "5px 8px 5px 0", fontWeight: 700, whiteSpace: "nowrap" }}>{d.label}</td>
                                        <td style={{ padding: "5px 8px 5px 0" }}>
                                            {d.formula}
                                            {d.rel ? <em style={{ color: "#b45309" }}> — relative: the best branch in the current selection always scores 100.</em> : null}
                                        </td>
                                        <td style={{ padding: "5px 0", textAlign: "right", fontWeight: 700 }}>{d.weight}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 8, lineHeight: 1.6 }}>
                            <strong>Overall</strong> is the six scores combined, each counted by the weight shown
                            above. A column showing <strong>—</strong> has no data for that branch, so it is left
                            out of the Overall instead of being counted as zero.
                            <br />
                            <strong>Academics</strong> and <strong>Results</strong> count online tests only —
                            marks entered for offline exams are not included. Both also cover every test on
                            record, so they are not limited by the date filter above.
                        </div>
                    </div>
                </details>
            </div>
        );
    }

    // REQ #12 — Test-wise batch performance trend (line per batch + direction chips).
    renderBatchTrend() {
        const bt = this.state.batchTrend || { testLabels: [], batches: [] };
        const batches = bt.batches || [];
        // ApexCharts throws a non-finite SVG transform when handed empty categories/series —
        // guard against the no-data state (nothing selected yet, or a scope with no exams).
        if (!batches.length || !(bt.testLabels || []).length) {
            return <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No test data available.</div>;
        }
        const allLabels = bt.testLabels || [];
        const palette = ["#7c3aed", "#06b6d4", "#16a34a", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#ec4899"];
        const colorOf = (name) => palette[Math.max(0, batches.findIndex((x) => x.name === name)) % palette.length];

        // Clicking a class (legend or chip) filters the chart to ONLY that class's exams:
        // keep the labels where that class actually has a value, and show only its series.
        // Clicking it again (or "Show all") clears the filter.
        const sel = this.state.btClass || null;
        let vLabels = allLabels, vBatches = batches;
        if (sel) {
            const b = batches.find((x) => x.name === sel);
            if (b) {
                const idxs = allLabels.map((_, i) => i).filter((i) => b.data[i] != null);
                vLabels = idxs.map((i) => allLabels[i]);
                vBatches = [{ ...b, data: idxs.map((i) => b.data[i]) }];
            }
        }

        // Each exam belongs to exactly one class, so the per-class series are mutually exclusive
        // (one has no value wherever the other does). ApexCharts fails to draw the FIRST series
        // when it carries nulls, so use 0 (not null) and STACK: each exam shows one bar in its
        // owning class's colour (the other contributes 0).
        const series = vBatches.map((b) => ({ name: b.name, data: (b.data || []).map((v) => (v == null ? 0 : v)) }));
        const barChartMinWidth = Math.max(600, vLabels.length * 70);
        const setClass = (name) => this.setState((st) => ({ btClass: st.btClass === name ? null : name }));
        const options = {
            chart: {
                type: "bar", stacked: true, height: 320, toolbar: { show: false }, fontFamily: "inherit",
                events: { legendClick: (ctx, i) => { const nm = series[i] && series[i].name; if (nm) setClass(nm); } },
            },
            plotOptions: { bar: { columnWidth: "60%", borderRadius: 4, borderRadiusApplication: "end" } },
            colors: vBatches.map((b) => colorOf(b.name)),
            dataLabels: { enabled: false },
            xaxis: { categories: vLabels, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            yaxis: { min: 0, max: 100, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            legend: { position: "top", labels: { colors: "#64748B" }, onItemClick: { toggleDataSeries: false } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 3 },
            fill: { opacity: 1 },
            tooltip: { shared: false, intersect: true, y: { formatter: (v) => (v ? `${v}%` : "-") } },
            noData: { text: "No data" },
        };
        const dirMeta = {
            up: { icon: "fal fa-arrow-up", color: "#16a34a", label: "Improving" },
            down: { icon: "fal fa-arrow-down", color: "#dc2626", label: "Declining" },
            flat: { icon: "fal fa-minus", color: "#64748b", label: "Stable" },
        };
        // Remount the chart when the visible labels/series change so ApexCharts fully rebuilds
        // its x-axis (it otherwise leaves stale exam labels behind).
        const chartKey = `${sel || "all"}::${vLabels.join("|")}::${vBatches.map((b) => b.name).join(",")}`;
        return (
            <>
                <div style={{ overflowX: "auto" }}>
                    <div style={{ minWidth: barChartMinWidth }}>
                        <ReactApexChart key={chartKey} options={options} series={series} type="bar" height={320} />
                    </div>
                </div>
                {batches.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Click a class to see only its exams:</span>
                        {sel && (
                            <span onClick={() => this.setState({ btClass: null })} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#334155", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 16, padding: "4px 10px", cursor: "pointer" }}>
                                <i className="fal fa-times" /> Show all
                            </span>
                        )}
                        {batches.map((b) => {
                            const m = dirMeta[b.direction] || dirMeta.flat;
                            const active = sel === b.name;
                            return (
                                <span key={b.name} onClick={() => setClass(b.name)}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: active ? 700 : 600, color: m.color, background: m.color + (active ? "28" : "14"), border: `1px solid ${m.color}${active ? "88" : "33"}`, borderRadius: 16, padding: "4px 10px", cursor: "pointer", opacity: sel && !active ? 0.5 : 1 }}>
                                    <i className={m.icon} /> {b.name}: {m.label}{b.change ? ` (${b.change > 0 ? "+" : ""}${b.change}%)` : ""}
                                </span>
                            );
                        })}
                    </div>
                )}
            </>
        );
    }

    // REQ #13 — Dropout risk scoring per batch (table sorted by risk %).
    renderDropoutRisk() {
        const rows = this.state.dropoutRisk || [];
        const th = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #e2e8f0" };
        const td = { fontSize: 13, color: "#334155", padding: "9px 10px", borderBottom: "1px solid #f1f5f9" };
        if (!rows.length) {
            return <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No batch risk data available.</div>;
        }
        return (
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                    <thead>
                        <tr>
                            <th style={th}>Batch</th><th style={{ ...th, textAlign: "center" }}>Students</th>
                            <th style={{ ...th, textAlign: "center" }}>High</th><th style={{ ...th, textAlign: "center" }}>Medium</th>
                            <th style={{ ...th, textAlign: "center" }}>On-track</th><th style={{ ...th, textAlign: "center" }}>Avg %</th>
                            <th style={{ ...th, textAlign: "center" }}>Risk %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const risk = Number(r.riskPct) || 0;
                            const riskColor = risk >= 40 ? "#dc2626" : risk >= 20 ? "#f59e0b" : "#16a34a";
                            return (
                                <tr key={r.batch}>
                                    <td style={{ ...td, fontWeight: 600, color: "#0f172a" }}>{r.batch}</td>
                                    <td style={{ ...td, textAlign: "center" }}>{r.total}</td>
                                    <td style={{ ...td, textAlign: "center", color: "#dc2626", fontWeight: 700 }}>{r.highRisk}</td>
                                    <td style={{ ...td, textAlign: "center", color: "#f59e0b", fontWeight: 700 }}>{r.mediumRisk}</td>
                                    <td style={{ ...td, textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{r.onTrack}</td>
                                    <td style={{ ...td, textAlign: "center" }}>{r.avgScore}%</td>
                                    <td style={{ ...td, textAlign: "center" }}>
                                        <span style={{ display: "inline-block", minWidth: 52, fontWeight: 700, color: "#fff", background: riskColor, borderRadius: 14, padding: "3px 10px", fontSize: 12 }}>{risk}%</span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
    // Skeleton for the dashboard body (KPI cards + charts) shown while a filter change
    // refetches — the header, filters and scope cards stay on screen and shimmer in place.
    renderDashboardBodyShimmer = () => {
        const bar = (h, w, mb) => <span className="mis-shim" style={{ display: "block", height: h, width: w, marginBottom: mb || 0 }} />;
        const chartCard = (h) => (
            <div className="mis-reports__chart-card">
                <div style={{ padding: 18 }}>
                    {bar(14, 220, 16)}
                    {bar(h, "100%")}
                </div>
            </div>
        );
        return (
            <div>
                <div className="mis-reports__kpi-grid">
                    {[0, 1, 2, 3].map((i) => (
                        <div className="mis-reports__kpi-card" key={i}>
                            <div className="mis-reports__kpi-card-left" style={{ width: "100%" }}>
                                {bar(11, 80, 12)}
                                {bar(24, 64, 12)}
                                {bar(10, 110)}
                            </div>
                        </div>
                    ))}
                </div>
                <Row className="mb-4">
                    <Col xs={12} lg={7}>{chartCard(260)}</Col>
                    <Col xs={12} lg={5}>{chartCard(260)}</Col>
                </Row>
                <Row className="mb-4">
                    <Col xs={12} lg={6}>{chartCard(260)}</Col>
                    <Col xs={12} lg={6}>{chartCard(260)}</Col>
                </Row>
                <Row className="mb-4">
                    <Col xs={12}>{chartCard(200)}</Col>
                </Row>
            </div>
        );
    };

    renderMisDashboard() {
        // Full-page shimmer only on the FIRST load (before any data exists). On filter/dropdown
        // changes we keep the header + filters + scope cards on screen (the scope counts shimmer)
        // and swap the body for a shimmer skeleton via renderDashboardBodyShimmer() below.
        if (this.state.loading && !this.state.reportsData) {
            return <PageCardShimmer filters={2} />;
        }

        const data = this.state.reportsData || {};
        const summary = data.overallSummary || {};
        const feeStatus = data.feeCollectionStatus || {};
        const trend = data.enrollmentTrend || [];
        // Prefer the dynamic (online) test-completion data; fall back to the report's.
        const tests = this.state.testCompletion || data.testCompletionStatus || [];

        // Academic performance by class — Avg Score % per class, from
        // getDropoutRiskByBatch. Replaces the faculty chart (now in the SP tab).
        const acadRows = this.state.dropoutRisk || [];
        const acadCats = acadRows.map((d) => d.batch);
        const classDistOptions = {
            chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
            plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
            colors: ["#7c3aed", "#16a34a"],
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 3 },
            legend: { position: "top" },
            xaxis: { categories: acadCats, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            yaxis: { max: 100, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            noData: { text: "No data" },
        };
        const classDistSeries = [
            { name: "Avg Score %", data: acadRows.map((d) => d.avgScore || 0) },
        ];

        // Format values
        const enrolled = summary.enrolledCount || 0;
        const dropouts = summary.dropoutCount || 0;
        const dropoutRate = enrolled + dropouts > 0 ? ((dropouts / (enrolled + dropouts)) * 100).toFixed(1) : "0.0";
        const feeCollectedPct = summary.paidAmountPercentage || 0;
        const pendingAmountLakhs = summary.pendingAmount ? (summary.pendingAmount / 100000).toFixed(1) : "0.0";
        const testsDone = summary.testsDoneCount || 0;
        const testsPending = summary.testsPendingCount || 0;
        console.log("testsDone", testsDone, testsPending, summary);

        // Avg academic performance — prefer the backend's institute-wide avgScore;
        // fall back to a student-weighted average of per-batch avg scores.
        const acadTotal = acadRows.reduce((a, d) => a + (d.total || 0), 0);
        const avgPerformance = (summary.avgScore != null && summary.avgScore > 0)
            ? Math.round(summary.avgScore)
            : (acadTotal > 0
                ? Math.round(acadRows.reduce((a, d) => a + (d.avgScore || 0) * (d.total || 0), 0) / acadTotal)
                : 0);

        return (
            <div className="mis-reports">
                {/* Header Section */}
                <div className="mis-reports__header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div className="mis-reports__header-title">
                        <h1>Management MIS Reports</h1>
                        <p>Weekly & monthly executive summary for college management</p>
                    </div>
                    <ExportButtons onExcel={this.handleExcel} onPdf={this.handlePdf} disabled={!(this.state.students || []).length} />
                </div>

                {/* Cascading scope filters + scope summary cards */}
                {this.renderScopeFilters()}
                {this.renderScopeCards()}
                {this.renderScopeDetail()}

                {this.state.loading ? this.renderDashboardBodyShimmer() : (<>
                {/* Top KPI Cards Grid */}
                <div className="mis-reports__kpi-grid">
                    {/* Card 1: Avg Performance — institute-wide academic average */}
                    <div className="mis-reports__kpi-card" style={{ cursor: "pointer" }}
                        onClick={() => this.goTab("studentAnalysis")}
                        title="View student performance analysis">
                        <div className="mis-reports__kpi-card-left">
                            <span className="label">Avg Performance</span>
                            <span className="value">{avgPerformance}%</span>
                            <span className="trend up">
                                <i className="fal fa-chart-line"></i> <span style={{ color: "#7c3aed", fontWeight: 700 }}>view analysis →</span>
                            </span>
                        </div>
                        <div className="mis-reports__kpi-card-icon blue">
                            <i className="fal fa-award"></i>
                        </div>
                    </div>

                    {/* Card 2: Dropouts */}
                    <div className="mis-reports__kpi-card">
                        <div className="mis-reports__kpi-card-left">
                            <span className="label">Dropouts</span>
                            <span className="value">{dropouts}</span>
                            <span className="trend down">
                                <i className="fal fa-arrow-down"></i> {dropoutRate}% rate
                            </span>
                        </div>
                        <div className="mis-reports__kpi-card-icon red">
                            <i className="fal fa-user-minus"></i>
                        </div>
                    </div>

                    {/* Card 3: Fee Collected */}
                    <div className="mis-reports__kpi-card">
                        <div className="mis-reports__kpi-card-left">
                            <span className="label">Fee Collected</span>
                            <span className="value">{feeCollectedPct}%</span>
                            <span className="trend down">
                                <i className="fal fa-exclamation-triangle"></i> ₹{pendingAmountLakhs}L due
                            </span>
                        </div>
                        <div className="mis-reports__kpi-card-icon orange">
                            <i className="fal fa-credit-card"></i>
                        </div>
                    </div>

                    {/* Card 4: Tests Done — click to view full exam details */}
                    <div className="mis-reports__kpi-card" style={{ cursor: "pointer" }}
                        onClick={() => this.goTab("exams")}
                        title="View exam details">
                        <div className="mis-reports__kpi-card-left">
                            <span className="label">Tests Done</span>
                            <span className="value">{testsDone}/{testsDone + testsPending}</span>
                            <span className="trend up">
                                <i className="fal fa-check-circle"></i> {testsPending} pending · <span style={{ color: "#7c3aed", fontWeight: 700 }}>view details →</span>
                            </span>
                        </div>
                        <div className="mis-reports__kpi-card-icon purple">
                            <i className="fal fa-flask"></i>
                        </div>
                    </div>
                </div>

                {/* Branch heat map — per-branch six-dimension scorecard */}
                <Row className="mb-4">
                    <Col xs={12}>{this.renderBranchHeatMap()}</Col>
                </Row>

                {/* REQ #12 — Test-wise Batch Performance Trend */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className="mis-reports__chart-card">
                            <div className="mis-reports__chart-card-header">
                                <h5 className="title">
                                    <i className="fal fa-chart-line"></i> Test-wise Batch Performance Trend
                                </h5>
                                {/* What each trend line represents: the class the exam was set
                                    for, or the exam it targets (NEET / JEE, via exam_type). */}
                                <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 8, padding: 2 }}>
                                    {[{ k: "class", label: "By Class" }, { k: "exam", label: "By Exam" }].map((g) => {
                                        const on = (this.state.btGroupBy || "class") === g.k;
                                        return (
                                            <button key={g.k}
                                                onClick={() => this.setState({ btGroupBy: g.k, btClass: null }, this.fetchReports)}
                                                style={{ border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: on ? "#7c3aed" : "transparent", color: on ? "#fff" : "#475569" }}>
                                                {g.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {this.renderBatchTrend()}
                        </div>
                    </Col>
                </Row>


                {/* Performance Benchmarks removed — the Comparisons tab compares by State, City,
                    Branch and Section directly, so this was the same analysis in a second place
                    and the two could disagree. */}

                {/* Enrollment trend + Fee status — moved to the bottom */}
                <Row className="mb-4">
                    {/* Enrollment vs Dropout Trend */}
                    <Col xs={12} md={6}>
                        <div className="mis-reports__chart-card h-100">
                            <div className="mis-reports__chart-card-header">
                                <h5 className="title">
                                    <i className="fal fa-chart-line"></i> Enrollment vs Dropout Trend
                                </h5>
                            </div>
                            {(trend && trend.length) ? (
                                <ReactApexChart
                                    options={this.getEnrollmentTrendChartOptions(trend)}
                                    series={this.getEnrollmentTrendChartSeries(trend)}
                                    type="line"
                                    height={350}
                                />
                            ) : (
                                <div style={{ textAlign: "center", padding: "70px 20px", color: "#94a3b8" }}>No enrollment data.</div>
                            )}
                        </div>
                    </Col>

                    {/* Fee Status Bar Chart */}
                    <Col xs={12} md={6}>
                        <div className="mis-reports__chart-card h-100">
                            <div className="mis-reports__chart-card-header">
                                <h5 className="title">
                                    <i className="fal fa-money-bill-wave-alt"></i> Fee Collection — Status & Pending Dues
                                </h5>
                                <div className="header-info">
                                </div>
                            </div>
                            {feeStatus && (Number(feeStatus.totalAmount) > 0 || Number(feeStatus.paidAmount) > 0 || Number(feeStatus.pendingAmount) > 0) ? (
                                <ReactApexChart
                                    options={this.getFeeStatusChartOptions(feeStatus)}
                                    series={this.getFeeStatusChartSeries(feeStatus)}
                                    type="bar"
                                    height={350}
                                />
                            ) : (
                                <div style={{ textAlign: "center", padding: "70px 20px", color: "#94a3b8" }}>No fee data.</div>
                            )}
                        </div>
                    </Col>
                </Row>
                </>)}
            </div>
        );
    }
}

export default withRouter(withApollo(ManagementMISReportsSection));
